/**
 * Phase 12 quiz designer tools — Phase 13 P3.
 *
 * Eight tools that together author every variant of the unified
 * `interactive_quiz` scene type.  The inspector panel on the right
 * shows only the tools relevant to the active scene's format/subject:
 *
 *   ALL interactive_quiz scenes:
 *     • Quiz Format   — pick how the quiz is presented (animated,
 *                       spotlight, or simple Q&A).
 *     • Quiz Subject  — what concept is being tested (coverage,
 *                       formation, personnel, etc.).
 *     • MCQ Editor    — author the question text and answer choices.
 *     • Teaching Video — attach a video that plays after the learner
 *                       answers (correct or wrong).
 *
 *   animated_board only:
 *     • Movement Duration — how long defenders take to reach their
 *                          post-snap assignments (1–3 seconds).
 *     • Offense Motion    — whether the offense stays still, mirrors,
 *                          or follows scripted routes during the snap.
 *     • Route Colors      — semantic stroke color for drawing routes
 *                          on the canvas.
 *
 *   static_spotlight only:
 *     • Spotlight Cycle — per-actor stop list with dwell timings.
 *
 *   any quiz with multiple_choice capability:
 *     • Suggested Distractors — auto-populate wrong answers from the
 *                              domain's coverage/formation catalog.
 */
import React, { useEffect, useMemo, useState } from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import {
  all, requiresSceneType, requiresFormat, requiresCapability,
} from '../predicates';

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const DEFAULT_FORMATS  = ['simple_qna', 'static_spotlight', 'animated_board'];
const DEFAULT_SUBJECTS = ['defender_technique', 'coverage', 'formation', 'personnel', 'concept'];

function readCatalogVocabulary(domainPack, key, fallback) {
  if (!domainPack || !domainPack.sceneTypeCatalog) return fallback;
  const row = domainPack.sceneTypeCatalog.find((r) => r.id === 'interactive_quiz');
  return (row && Array.isArray(row[key]) && row[key].length > 0) ? row[key] : fallback;
}

// ────────────────────────────────────────────────────────────────────
// 1. QuizFormatPicker — TOP_TOOLBAR
// ────────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
  simple_qna:       'Simple Q&A — text question with no field animation',
  static_spotlight: 'Spotlight — camera cycles through individual players',
  animated_board:   'Animated Board — defenders move to reveal the coverage',
};

function QuizFormatPicker() {
  const { scene, setDomainMetadata, domainPack } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const formats = readCatalogVocabulary(domainPack, 'quiz_formats', DEFAULT_FORMATS);

  return (
    <div className="designer-tool" data-tool-id="platform.quiz_format_picker">
      <label className="designer-tool__label" htmlFor="quiz-format-picker">Quiz format</label>
      <select
        id="quiz-format-picker"
        className="designer-tool__input"
        aria-label="Quiz format"
        value={meta.quiz_format || ''}
        onChange={(e) => setDomainMetadata({ quiz_format: e.target.value })}
      >
        <option value="" disabled>Pick a format…</option>
        {formats.map((f) => (
          <option key={f} value={f}>{FORMAT_LABELS[f] || f.replace(/_/g, ' ')}</option>
        ))}
      </select>
      <span className="designer-tool__hint">
        Controls how the quiz is presented to the learner.
      </span>
    </div>
  );
}

export const quizFormatPicker = new DesignerTool({
  id:         'platform.quiz_format_picker',
  label:      'Quiz format',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('interactive_quiz'),
  Component:  QuizFormatPicker,
  weight:     30,
  helpText:   'Pick the presentation format from the catalog\'s quiz_formats vocabulary.',
});

// ────────────────────────────────────────────────────────────────────
// 2. QuizSubjectPicker — TOP_TOOLBAR
// ────────────────────────────────────────────────────────────────────

const SUBJECT_LABELS = {
  defender_technique: 'Defender Technique',
  coverage:           'Coverage (Cover 1, 2, 3, 4…)',
  formation:          'Formation recognition',
  personnel:          'Personnel package (11, 21, 12…)',
  concept:            'Scheme concept',
};

function QuizSubjectPicker() {
  const { scene, setDomainMetadata, domainPack } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const subjects = readCatalogVocabulary(domainPack, 'quiz_subjects', DEFAULT_SUBJECTS);

  return (
    <div className="designer-tool" data-tool-id="platform.quiz_subject_picker">
      <label className="designer-tool__label" htmlFor="quiz-subject-picker">What concept is being tested?</label>
      <select
        id="quiz-subject-picker"
        className="designer-tool__input"
        aria-label="Quiz subject"
        value={meta.quiz_subject || ''}
        onChange={(e) => setDomainMetadata({ quiz_subject: e.target.value })}
      >
        <option value="" disabled>Pick a subject…</option>
        {subjects.map((s) => (
          <option key={s} value={s}>{SUBJECT_LABELS[s] || s.replace(/_/g, ' ')}</option>
        ))}
      </select>
      <span className="designer-tool__hint">
        Used to group quiz analytics and auto-suggest distractors.
      </span>
    </div>
  );
}

export const quizSubjectPicker = new DesignerTool({
  id:         'platform.quiz_subject_picker',
  label:      'Quiz subject',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('interactive_quiz'),
  Component:  QuizSubjectPicker,
  weight:     31,
  helpText:   'Pick the subject from the catalog\'s quiz_subjects vocabulary.',
});

// ────────────────────────────────────────────────────────────────────
// 3. MovementDurationSlider — RIGHT_INSP, animated_board only
// ────────────────────────────────────────────────────────────────────

const MOVEMENT_MIN = 1000;
const MOVEMENT_MAX = 3000;

function MovementDurationSlider() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const movement = meta.movement || {};
  const duration = movement.duration_ms != null ? movement.duration_ms : 1500;

  const set = (val) => {
    const clamped = Math.max(MOVEMENT_MIN, Math.min(MOVEMENT_MAX, val));
    setDomainMetadata({ movement: { ...movement, duration_ms: clamped } });
  };

  return (
    <div className="designer-tool" data-tool-id="platform.movement_duration_slider">
      <label className="designer-tool__label" htmlFor="movement-duration">
        How long defenders take to reach their zones: <strong style={{ color: 'var(--fg-text)' }}>{duration / 1000}s</strong>
      </label>
      <input
        id="movement-duration"
        type="range"
        className="designer-tool__input"
        min={MOVEMENT_MIN}
        max={MOVEMENT_MAX}
        step="100"
        value={duration}
        aria-label="Defender movement duration in milliseconds"
        onChange={(e) => set(parseInt(e.target.value, 10))}
      />
      <span className="designer-tool__hint">
        1.0 s = fast reveal (advanced learners) · 2.0 s = recommended · 3.0 s = slow (beginners)
      </span>
    </div>
  );
}

export const movementDurationSlider = new DesignerTool({
  id:         'platform.movement_duration_slider',
  label:      'Movement duration',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresSceneType('interactive_quiz'),
    requiresFormat('animated_board'),
  ),
  Component:  MovementDurationSlider,
  weight:     70,
  helpText:   'Set the time defenders take to move into their post-snap positions.',
});

// ────────────────────────────────────────────────────────────────────
// 4. OffenseMotionRadio — RIGHT_INSP, animated_board only
// ────────────────────────────────────────────────────────────────────

const OFFENSE_MOTION_MODES = [
  { id: 'static',   label: 'Static — offense stays in pre-snap positions (most common)' },
  { id: 'mirror',   label: 'Mirror — offense mirrors defense movement during snap' },
  { id: 'scripted', label: 'Scripted — coach draws custom routes per offensive player' },
];

function OffenseMotionRadio() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const movement = meta.movement || {};
  const current = movement.offense_motion || 'static';

  const set = (mode) => {
    setDomainMetadata({ movement: { ...movement, offense_motion: mode } });
  };

  return (
    <div className="designer-tool" data-tool-id="platform.offense_motion_radio">
      <h3 className="designer-tool__heading">Offense behavior during snap</h3>
      {OFFENSE_MOTION_MODES.map((m) => (
        <label key={m.id} className="designer-tool__row designer-tool__row--inline">
          <input
            type="radio"
            name="offense-motion"
            value={m.id}
            checked={current === m.id}
            aria-label={m.label}
            onChange={() => set(m.id)}
          />
          <span style={{ fontSize: '0.8rem' }}>{m.label}</span>
        </label>
      ))}
    </div>
  );
}

export const offenseMotionRadio = new DesignerTool({
  id:         'platform.offense_motion_radio',
  label:      'Offense motion',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresSceneType('interactive_quiz'),
    requiresFormat('animated_board'),
  ),
  Component:  OffenseMotionRadio,
  weight:     71,
  helpText:   'Choose how the offense behaves during the post-snap movement window.',
});

// ────────────────────────────────────────────────────────────────────
// 5. DistractorPicker — RIGHT_INSP
// ────────────────────────────────────────────────────────────────────
//
// Auto-suggests sibling answers from the domain pack's vocabulary
// catalog. For coverage scenes, the catalog ships
// `coverage_catalog.entries[].id`; for formation scenes,
// `formation_catalog.entries[].id`; etc. The tool reads the
// active subject and looks up the sibling key.

const SUBJECT_TO_CATALOG_KEY = {
  coverage:   'coverage_catalog',
  formation:  'formation_catalog',
  personnel:  'personnel_catalog',
};

function DistractorPicker() {
  const { scene, setDomainMetadata, domainPack } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const subject = meta.quiz_subject;
  const correctId = (meta.question_template && meta.question_template.correct_answer_id) || '';

  const catalogKey = SUBJECT_TO_CATALOG_KEY[subject];
  const catalog = (domainPack && catalogKey && domainPack[catalogKey]) || null;
  const candidates = (catalog && catalog.entries) || [];

  const distractors = (meta.question_template && meta.question_template.distractor_ids) || [];

  const toggleDistractor = (id) => {
    const next = distractors.includes(id)
      ? distractors.filter((d) => d !== id)
      : [...distractors, id];
    setDomainMetadata({
      question_template: {
        ...(meta.question_template || {}),
        distractor_ids: next,
      },
    });
  };

  if (!catalog) {
    return (
      <div className="designer-tool" data-tool-id="platform.distractor_picker">
        <h3 className="designer-tool__heading">Wrong answer suggestions</h3>
        <span className="designer-tool__empty">
          No catalog available for subject <code>{subject || '<unset>'}</code> — add wrong answers manually in the MCQ editor below.
        </span>
      </div>
    );
  }

  return (
    <div className="designer-tool" data-tool-id="platform.distractor_picker">
      <h3 className="designer-tool__heading">Wrong answer suggestions</h3>
      <span className="designer-tool__hint">
        Check the answers you want to include as wrong choices. These come from the {subject} catalog so they are plausible but incorrect.
      </span>
      {candidates
        .filter((c) => c.id !== correctId)
        .map((c) => (
          <label key={c.id} className="designer-tool__row designer-tool__row--inline">
            <input
              type="checkbox"
              checked={distractors.includes(c.id)}
              aria-label={`Use ${c.label || c.id} as wrong answer`}
              onChange={() => toggleDistractor(c.id)}
            />
            <span style={{ fontSize: '0.8rem' }}>{c.label || c.id}</span>
          </label>
        ))}
    </div>
  );
}

export const distractorPicker = new DesignerTool({
  id:         'platform.distractor_picker',
  label:      'Distractors',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresSceneType('interactive_quiz'),
    requiresCapability('multiple_choice'),
  ),
  Component:  DistractorPicker,
  weight:     72,
  helpText:   'Auto-suggest plausible wrong answers from the domain\'s sibling catalog.',
});

// ────────────────────────────────────────────────────────────────────
// 6. SpotlightCycleEditor — RIGHT_INSP, static_spotlight only
// ────────────────────────────────────────────────────────────────────

function SpotlightCycleEditor() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const cycle = (meta.spotlight && meta.spotlight.cycle) || [];

  const setCycle = (next) => {
    setDomainMetadata({
      spotlight: { ...(meta.spotlight || {}), cycle: next },
    });
  };

  const updateStop = (idx, patch) => {
    setCycle(cycle.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addStop = () => {
    setCycle([
      ...cycle,
      { actor_id: '', label: '', dwell_ms: 2000, question_template_id: '' },
    ]);
  };

  const removeStop = (idx) => {
    setCycle(cycle.filter((_, i) => i !== idx));
  };

  return (
    <div className="designer-tool" data-tool-id="platform.spotlight_cycle_editor">
      <h3 className="designer-tool__heading">Spotlight cycle</h3>
      {cycle.length === 0 && (
        <span className="designer-tool__empty">Add at least one stop.</span>
      )}
      {cycle.map((stop, i) => (
        <div
          key={i}
          className="designer-tool__row"
          data-spotlight-stop={i}
        >
          <div className="designer-tool__row designer-tool__row--inline">
            <input
              type="text"
              className="designer-tool__input"
              placeholder="actor_id"
              value={stop.actor_id || ''}
              aria-label={`Stop ${i + 1} actor id`}
              onChange={(e) => updateStop(i, { actor_id: e.target.value })}
            />
            <input
              type="text"
              className="designer-tool__input"
              placeholder="label"
              value={stop.label || ''}
              aria-label={`Stop ${i + 1} label`}
              onChange={(e) => updateStop(i, { label: e.target.value })}
            />
          </div>
          <div className="designer-tool__row designer-tool__row--inline">
            <input
              type="number"
              className="designer-tool__input"
              placeholder="dwell ms"
              value={stop.dwell_ms || 0}
              step="500"
              min="500"
              max="10000"
              aria-label={`Stop ${i + 1} dwell milliseconds`}
              onChange={(e) => updateStop(i, { dwell_ms: parseInt(e.target.value, 10) || 0 })}
            />
            <input
              type="text"
              className="designer-tool__input"
              placeholder="question template id"
              value={stop.question_template_id || ''}
              aria-label={`Stop ${i + 1} question template id`}
              onChange={(e) => updateStop(i, { question_template_id: e.target.value })}
            />
            <button
              type="button"
              className="designer-tool__button designer-tool__button--icon"
              aria-label={`Remove stop ${i + 1}`}
              onClick={() => removeStop(i)}
            >×</button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="designer-tool__button"
        onClick={addStop}
        aria-label="Append spotlight stop"
      >
        + Stop
      </button>
    </div>
  );
}

export const spotlightCycleEditor = new DesignerTool({
  id:         'platform.spotlight_cycle_editor',
  label:      'Spotlight cycle',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresSceneType('interactive_quiz'),
    requiresFormat('static_spotlight'),
  ),
  Component:  SpotlightCycleEditor,
  weight:     73,
  helpText:   'Author the per-actor spotlight cycle for static_spotlight quizzes.',
});

// ────────────────────────────────────────────────────────────────────
// 7. TeachingVideoLink — RIGHT_INSP
// ────────────────────────────────────────────────────────────────────
//
// Loads the same-domain video scene list from the API. The endpoint
// (`GET /scenario-os/scenes?domain=<id>&scene_type=video&fields=scene_id,display_name`)
// is read-only; the tool falls back to a manual text input if the
// API call fails (offline preview, dev fixtures).

function TeachingVideoLink() {
  const { scene, setDomainMetadata, domainPack } = useDesigner();
  const [videos, setVideos] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const domain = (domainPack && domainPack.id) || (scene && scene.domain_id);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!domain) return;
      try {
        const apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SCENARIO_OS_API)
          || '/scenario-os';
        const resp = await fetch(
          `${apiBase}/scenes?domain=${encodeURIComponent(domain)}&scene_type=video&fields=scene_id,display_name`,
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setVideos(Array.isArray(data) ? data : (data.scenes || []));
      } catch (err) {
        if (!cancelled) setLoadError(err.message || String(err));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [domain]);

  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const linked = meta.teaching_video_id || '';

  if (loadError || videos == null) {
    return (
      <div className="designer-tool" data-tool-id="platform.teaching_video_link">
        <label className="designer-tool__label" htmlFor="teaching-video-id">Teaching video id</label>
        <input
          id="teaching-video-id"
          type="text"
          className="designer-tool__input"
          value={linked}
          aria-label="Teaching video scene id"
          placeholder="scene_id of the video scene"
          onChange={(e) => setDomainMetadata({ teaching_video_id: e.target.value })}
        />
        {loadError && (
          <span className="designer-tool__hint">
            (catalog unreachable — entered manually)
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="designer-tool" data-tool-id="platform.teaching_video_link">
      <label className="designer-tool__label" htmlFor="teaching-video-select">Teaching video</label>
      <select
        id="teaching-video-select"
        className="designer-tool__input"
        value={linked}
        aria-label="Teaching video"
        onChange={(e) => setDomainMetadata({ teaching_video_id: e.target.value })}
      >
        <option value="">— None —</option>
        {videos.map((v) => (
          <option key={v.scene_id} value={v.scene_id}>{v.display_name || v.scene_id}</option>
        ))}
      </select>
    </div>
  );
}

export const teachingVideoLink = new DesignerTool({
  id:         'platform.teaching_video_link',
  label:      'Teaching video',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresSceneType('interactive_quiz'),
  Component:  TeachingVideoLink,
  weight:     74,
  helpText:   'Attach a teaching-video scene from the same domain as remediation.',
});

// ────────────────────────────────────────────────────────────────────
// 8. RouteColorSwatch — CANVAS_OVERLAY, animated_board only
// ────────────────────────────────────────────────────────────────────

const ROUTE_COLORS = [
  { id: 'red',    label: 'Defender movement' },
  { id: 'blue',   label: 'D-line forward' },
  { id: 'yellow', label: 'Coverage assignment' },
];

function RouteColorSwatch() {
  const { eventBus } = useDesigner();
  const [active, setActive] = useState('red');

  const pick = (id) => {
    setActive(id);
    eventBus.emit('quiz.route_color_changed', { color: id });
  };

  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="platform.route_color_swatch"
      role="toolbar"
      aria-label="Route color swatches"
    >
      {ROUTE_COLORS.map((c) => (
        <button
          type="button"
          key={c.id}
          className={
            'designer-tool__button designer-tool__button--swatch' +
            (active === c.id ? ' is-active' : '')
          }
          aria-label={c.label}
          aria-pressed={active === c.id}
          title={c.label}
          style={{ backgroundColor: c.id }}
          data-color={c.id}
          onClick={() => pick(c.id)}
        />
      ))}
    </div>
  );
}

export const routeColorSwatch = new DesignerTool({
  id:         'platform.route_color_swatch',
  label:      'Route colors',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: all(
    requiresSceneType('interactive_quiz'),
    requiresFormat('animated_board'),
  ),
  Component:  RouteColorSwatch,
  weight:     53,
  helpText:   'Pick the semantic color for the next route stroke.',
});

// ────────────────────────────────────────────────────────────────────
// Bulk export
// ────────────────────────────────────────────────────────────────────

export const quizDesignerTools = [
  quizFormatPicker,
  quizSubjectPicker,
  movementDurationSlider,
  offenseMotionRadio,
  distractorPicker,
  spotlightCycleEditor,
  teachingVideoLink,
  routeColorSwatch,
];

export default quizDesignerTools;
